/**
 * ResetPasswordPage Component
 * 
 * Allows users to set a new password using a reset token from email.
 * Token is extracted from URL query parameters.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onBack - Callback to go back to previous screen
 * @param {Function} props.onSuccess - Callback when reset succeeds
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function ResetPasswordPage({ onBack, onSuccess }) {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  // Extract token from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Invalid reset link');
      return;
    }

    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: newPassword 
      });
      setResetComplete(true);
      toast.success('Password reset successful!');
    } catch (error) {
      console.error('Password reset failed:', error);
      toast.error(error.response?.data?.detail || 'Password reset failed. Link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-center relative">
            <button 
              onClick={onBack}
              className="absolute left-0 top-0 text-orange-600 hover:text-orange-700 flex items-center gap-1 text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_bawarchi-catering-2/artifacts/6e6yv8t6_caterlog-high-resolution-logo-transparent.png" 
              alt="CaterLog" 
              className="h-14 mx-auto mb-4"
            />
            <CardTitle className="text-2xl mb-2">Reset Password</CardTitle>
            <p className="text-sm text-muted">Choose a new password for your account</p>
          </div>
        </CardHeader>
        <CardContent>
          {!resetComplete ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-bawarchi">New Password * (min 8 characters)</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="input-bawarchi pr-12"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Create a strong password"
                    disabled={loading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="label-bawarchi">Confirm Password *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="input-bawarchi pr-12"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                className="btn-bawarchi-primary w-full"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Password Reset Successful!</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Your password has been changed. You can now sign in with your new password.
                </p>
              </div>
              <button
                onClick={onSuccess}
                className="btn-bawarchi-primary w-full"
              >
                Go to Sign In
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ResetPasswordPage;